import {Button, Col, Row, Table, Tooltip} from "antd";
import {useNavigate, useLocation} from "react-router-dom";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {FilterOutlined, SearchOutlined} from "@ant-design/icons";
import TagList from "./action-table-taglist";
import FilterDropdown from "./action-table-filterdropdown";
import SearchDropdown from "./action-table-searchdropdown";
import EditableRow from "./editable-row";
import EditableCell from "./editable-cell";
import DropdownButton from "./dropdown-button";
import ConfirmButton from "../util/confirm-button";
import styled from "styled-components";

const StyledTagList = styled(TagList)`
  span.ant-tag {
    font-size: 120%;
    line-height: 30px;
  }
`

const ActionTable = ({isParentLoading, searchBar, buttonRow, tableData, scroll, queryParams, setQueryParams, pageSizeOptions, rowKey = 'id', handleSave = () => {}}:any) => {
    const filterComponents = useRef<any>({});

    console.log("Action table query params", queryParams);
   
    const searchText = useMemo(() => queryParams?.search || '', [queryParams]);

    const assembleFilter = useCallback(filterObj => {
      let transformedFilter = {}
      Object.entries(filterObj).forEach(([key, value]: any) => {
          transformedFilter[key] = {
              c: value.comparator,
              v: value.value,
              v2: value.value2,
              type: value.type
          };
      });
      return transformedFilter;
    }, [])

    // update stringifiedQuery
    const stringifiedQueryParams = useMemo(() => {
        return {
            o: queryParams.offset,
            c: queryParams.pageSize,
            ...(queryParams.sort && {st: JSON.stringify(queryParams.sort)}),
            f: queryParams.filter ? JSON.stringify(assembleFilter(queryParams.filter)) : undefined,
            s: searchText && searchText.length ? searchText : undefined
        };
    }, [queryParams, searchText, assembleFilter]);

    const stringifiedFilter = useMemo(() => queryParams.filter ? JSON.stringify(assembleFilter(queryParams.filter)) : undefined, [queryParams, assembleFilter])
    
    const handleResetFilter = useCallback((dataIndex) => {
      // get previous filters without deleted one
      const newFilter = {...(queryParams?.filter ? queryParams.filter : {})};
      delete newFilter[dataIndex];

      setQueryParams({
          ...queryParams,
          offset: 0,
          filter: newFilter
      })

      // clear filter value in filter component
      filterComponents?.current[dataIndex]?.clearFilterValue();
  }, [queryParams, setQueryParams]);

  const setSearchText = useCallback((search) => {
    setQueryParams({
      ...queryParams,
      search
    })
  }, [queryParams, setQueryParams]);

    // update tags
    const tags = useMemo(() => {
        const t:any = [];
        if (queryParams?.filter) {
            Object.entries(queryParams?.filter).forEach(entry => {
                const [key, value]:any = entry;
                t.push({
                    title: value?.title,
                    value: value?.displayValue ? value?.displayValue : value?.originalValue,
                    onReset: () => handleResetFilter(key)
                })
            });
        }
        return t;
    }, [queryParams?.filter, handleResetFilter])

    // data query endpoint
    const {
        data: queryData,
        isFetching: isQueryDataFetching,
    } = tableData?.dataSource?.pageableQueryEndpoint(stringifiedQueryParams);


    // on error
    //useEffect(() => queryDataError && ShowDefaultQueryErrorNotification(queryDataError), [queryDataError])

    // table data
    const dataSource = useMemo(() => tableData?.dataSource?.queryResultMapping(queryData), [queryData, tableData?.dataSource]);

    const handleAddFilter = useCallback((dataIndex, title, filterType, value, displayValue) => {
        if (!value) return;

        const newFilter = {...(queryParams?.filter ? queryParams.filter : {})};

        console.log("Display value", title, value, displayValue)

        switch (filterType) {
            case 'string':
                newFilter[dataIndex] = {
                    comparator: 'ILIKE',
                    value: `%${value}%`,
                    originalValue: `${value}`,
                    displayValue: displayValue,
                    title: title,
                }
                break;
            case 'stringExact':
                newFilter[dataIndex] = {
                    comparator: 'EQ',
                    value: `${value}`,
                    originalValue: `${value}`,
                    displayValue: displayValue,
                    title: title,
                }
                break;
            case 'int':
                newFilter[dataIndex] = {
                    comparator: 'EQ',
                    value: `${value}`,
                    originalValue: `${value}`,
                    displayValue: displayValue,
                    title: title,
                }
                break;
            case 'boolean':
                newFilter[dataIndex] = {
                    comparator: 'EQ',
                    value: `${true}` === value,
                    originalValue: `${value}`,
                    displayValue: value === `${true}` ? 'Ja' : 'Nein',
                    title: title,
                }
        }

        console.log("Adding filter", value, newFilter);

        setQueryParams({
            ...queryParams,
            offset: 0,
            filter: newFilter
        });
    }, [queryParams, setQueryParams]);

    

    function onTableChange(pagination, filters, sorter) {
        const newQueryParams = {...queryParams};

        // pagination
        if (pagination) {
            newQueryParams.offset = (pagination.current - 1) * pagination.pageSize;
            newQueryParams.pageSize = pagination.pageSize
        } else {
            newQueryParams.offset = 0;
            newQueryParams.pageSize = 10;
        }

        // sorter
        if (sorter) {
            switch (sorter.order) {
                case 'ascend':
                    newQueryParams.sort = {[sorter.column.dataIndex]: 'asc'};
                    break;
                case 'descend':
                    newQueryParams.sort = {[sorter.column.dataIndex]: 'desc'};
                    break;
                case undefined:
                    newQueryParams.sort = '';
                    break;
            }
        }

        setQueryParams(newQueryParams);
    }

    /*
    table props
     */

    // column search
    const getColumnSearchProps = useCallback((dataIndex, filterType, handleFilter, handleFilterReset) => ({
        filterDropdown: ({confirm, clearFilters}) =>
            (<SearchDropdown
                ref={(el:any) => filterComponents.current[dataIndex] = el}
                defaultValue={queryParams?.filter[dataIndex]?.originalValue}
                confirm={confirm}
                clearFilters={clearFilters}
                dataIndex={dataIndex}
                filterType={filterType}
                handleFilter={handleFilter}
                handleFilterReset={handleFilterReset}
            />)
        ,
        filterIcon: () => (
            <SearchOutlined style={{color: queryParams?.filter[dataIndex]?.originalValue ? '#1890ff' : undefined}}/>),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                //setTimeout(() => true || this.searchInput.select(), 100);
            }
        },
    }), [queryParams?.filter]);

    // column filter
    const getFilterProps = useCallback((dataIndex, type, typeAttrs, handleFilter, handleFilterReset) => ({
        filterDropdown: ({confirm, clearFilters}) =>
            (<FilterDropdown
                ref={(el:any) => filterComponents.current[dataIndex] = el}
                defaultValue={queryParams?.filter[dataIndex]?.originalValue}
                confirm={confirm}
                clearFilters={clearFilters}
                type={type}
                typeAttrs={typeAttrs}
                handleFilter={handleFilter}
                handleFilterReset={handleFilterReset}
            />)
        ,
        filterIcon: () => (
            <FilterOutlined style={{color: queryParams?.filter[dataIndex]?.originalValue ? '#1890ff' : undefined}}/>),
        onFilterDropdownOpenChange: visible => {
            if (visible) {
                //setTimeout(() => true || this.searchInput.select(), 100);
            }
        },
    }), [queryParams?.filter])

    // table columns
    const columns = useMemo(() => {
        // add columns
        const cols = tableData?.columns?.map(column => ({
            ...column,
            // default sort
            defaultSortOrder: queryParams?.sort[column.dataIndex] ? (queryParams?.sort[column.dataIndex] === 'asc' ? 'ascend' : 'descend') : undefined,
            // filterType is int or string
            ...(column.filter && column.type && typeof column.type !== 'object' ?
                getColumnSearchProps(
                    column.dataIndex,
                    column.type,
                    (selectedKeys) => handleAddFilter(column.dataIndex, column.title, column.type, selectedKeys, selectedKeys),
                    () => handleResetFilter(column.dataIndex))
                : []),
            // filterType is enum object
            ...(column.filter && column.type && column.type === 'enum' ?
                {
                    ...getFilterProps(
                        column.dataIndex,
                        column.type,
                        column.typeAttrs,
                        (selectedKeys) => handleAddFilter(column.dataIndex, column.title, 'stringExact', selectedKeys, column.type[selectedKeys]),
                        () => handleResetFilter(column.dataIndex)),
                    //render: (text) => column.type[text],
                }
                : []),
            ...(column.filter && column.type && column.type === 'separatedList' ?
                {
                    ...getFilterProps(
                        column.dataIndex,
                        column.type,
                        column.typeAttrs,
                        (selectedKeys) => handleAddFilter(column.dataIndex, column.title, 'string', selectedKeys, column.type[selectedKeys]),
                        () => handleResetFilter(column.dataIndex)),
                    //render: (text) => column.type[text],
                }
                : []),
            ...(column.filter && column.type && column.type === 'boolean' ?
                {
                    ...getFilterProps(
                        column.dataIndex,
                        column.type,
                        column.typeAttrs,
                        (selectedKeys) => handleAddFilter(column.dataIndex, column.title, 'boolean', selectedKeys, column.type[selectedKeys]),
                        () => handleResetFilter(column.dataIndex)),
                    //render: (text) => column.type[text],
                }
                : []),
            onCell: record => ({
              record,
              editable: column.editable,
              dataIndex: column.dataIndex,
              title: column.title,
              handleSave,
              type: column.type,
              typeAttrs: column.typeAttrs,
            }),

        })) || [];

        // add last column actions
        if (tableData?.actions?.length > 0) {
            cols.push({
                title: 'Aktionen',
                key: 'actions',
                sorter: false,
                width: '15%',
                render: (_, record) => <div style={{ display: "flex", flexDirection: "row"}}>{ tableData?.actions?.filter(action => !action.when || action.when(record)).map(action =>
                    <Tooltip key={`${record.id}-${action.caption}-tooltip`} title={action?.title}>
                      {
                        action.confirmContent ? 
                        <ConfirmButton key={`${record.id}-${action.caption}-button`}
                                icon={action?.icon}
                                type={action?.type}
                                confirmTitle={action.confirmTitle(record)}
                                confirmContent={action.confirmContent(record)}
                                disabled={action?.disabled && action?.disabled(record)}
                                onClick={() => action?.onClick(record)}
                                danger={action?.danger}
                                style={{marginRight: 8}}>{action?.caption}</ConfirmButton>
                        :
                        <Button key={`${record.id}-${action.caption}-button`}
                                icon={action?.icon}
                                type={action?.type}
                                disabled={action?.disabled && action?.disabled(record)}
                                onClick={() => action?.onClick(record)}
                                danger={action?.danger}
                                style={{marginRight: 8}}>{action?.caption}</Button>
                      }
                    </Tooltip>) }</div>
            })
        }

        return cols;
    }, [tableData?.columns, tableData?.actions, handleSave,
         getColumnSearchProps, getFilterProps, handleAddFilter, handleResetFilter, queryParams?.sort
    ])

    // table pagination
    const pagination = useMemo(() => ({
        current: ((queryData?.offset / queryData?.pageSize) + 1 || 1),
        pageSize: queryData?.pageSize,
        total: queryData?.totalCnt,
        pageSizeOptions: pageSizeOptions || [5, 10, 20, 50, 100],
        showSizeChanger: true,
        showTotal: (total) => `${total} item(s) found`,
    }), [queryData?.offset, queryData?.pageSize, queryData?.totalCnt, pageSizeOptions])

    const components = {
      body: {
        row: EditableRow,
        cell: EditableCell,
      },
    };

    const buttonRowButton = useCallback((buttonDesc) => {     
      if(buttonDesc.submenu) {
        return <DropdownButton {...buttonDesc} />
      } else {
        return <Button key={buttonDesc.text}
          type={buttonDesc.buttonType}
          style={{marginLeft: 4}}
          onClick={buttonDesc.onClick}>{buttonDesc.text}</Button>
      }
    }, [])

    const buttonRowButtons = useMemo(() => buttonRow?.buttons?.map(button => buttonRowButton(button)), [buttonRow?.buttons, buttonRowButton])

    const SearchBar = searchBar

    return (
        <div style={{width: '100%', height: '100%', display: 'flex', flexDirection: "column" }} >
            { searchBar && <SearchBar setSearchText={setSearchText} stringifiedFilter={stringifiedFilter} /> }
            <Row style={{paddingLeft: 7, paddingTop: 8, paddingRight: 7}} align="middle">

                <Col style={{}} span={8}>
                    <StyledTagList tags={tags}/>
                </Col>

                <div style={{ height: 23 }} />

                <Col span={8} offset={8}>
                    {buttonRow?.buttons?.length > 0 &&
                        <Row justify="end">{ buttonRowButtons }</Row>}
                </Col>

            </Row>

            <div style={{ flex: 1, marginTop: 8 }}>
              <Table
                  components={components}
                  columns={columns}
                  dataSource={dataSource}
                  rowKey={rowKey}
                  loading={isParentLoading || isQueryDataFetching}
                  onChange={onTableChange}
                  pagination={pagination}
                  expandable={{showExpandColumn: false}}
                  scroll={scroll}
              />
            </div>

        </div>
    );
}

export const ActionTablePath = ({ tableData, storageKey, ...rest }: any) => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlSearchParams:any = useMemo(() => new URLSearchParams(location.search), [location.search]);

  
  /*
    This object holds the current data for the query params.
    Upon change the useMemo function is called for stringifiedQueryParams, which are already passed for data fetching
    and will be updated on change.
     */
  const queryParams:any = useMemo(() => ({
      offset: urlSearchParams.has('offset') ? parseInt(urlSearchParams.get('offset')) : 0,
      pageSize: urlSearchParams.has('pagesize') ? parseInt(urlSearchParams.get('pagesize')) : 10,
      sort: urlSearchParams.has('sort') ? JSON.parse(urlSearchParams.get('sort')) : {[tableData?.defaultSortKey]: tableData?.defaultSortDirection ?? 'asc'},
      filter: urlSearchParams.has('filter') ? JSON.parse(atob(urlSearchParams.get('filter'))) : {},
      search: urlSearchParams.has('search') ? urlSearchParams.get('search') : '',
      // s: '', // search
  }), [urlSearchParams, tableData])

  // update URL search params
  const setQueryParams = useCallback((newQueryParams) => {
    console.log("Setting new query params", newQueryParams);
    let search = `?offset=${newQueryParams.offset}&pagesize=${newQueryParams.pageSize}`;
    if (newQueryParams.sort) {
      search += `&sort=${JSON.stringify(newQueryParams.sort)}`;
    }
    if (newQueryParams.filter) {
      search += `&filter=${btoa(JSON.stringify(newQueryParams.filter))}`;
    }
    if (newQueryParams.search) {
      search += `&search=${encodeURIComponent(newQueryParams.search)}`
    }
    navigate(`${location.pathname}${search}`)
  }, [navigate, location.pathname]);

  useEffect(() => {
    console.log("URL search params changed", urlSearchParams, storageKey);
    if(storageKey) {
      if(!urlSearchParams.get('offset') && !urlSearchParams.get('pagesize') && !urlSearchParams.get('sort') && !urlSearchParams.get('filter') && !urlSearchParams.get('search')) {
        const savedQueryParams = sessionStorage.getItem(`actionTableQueryParams-${storageKey}`);
        if(savedQueryParams) {
          const parsedQueryParams = JSON.parse(savedQueryParams);
          setQueryParams(parsedQueryParams);
        }
      } else {
        sessionStorage.setItem(`actionTableQueryParams-${storageKey}`, JSON.stringify({
          offset: urlSearchParams.has('offset') ? parseInt(urlSearchParams.get('offset')) : 0,
          pageSize: urlSearchParams.has('pagesize') ? parseInt(urlSearchParams.get('pagesize')) : 10,
          sort: urlSearchParams.has('sort') ? JSON.parse(urlSearchParams.get('sort')) : {[tableData?.defaultSortKey]: tableData?.defaultSortDirection ?? 'asc'},
          filter: urlSearchParams.has('filter') ? JSON.parse(atob(urlSearchParams.get('filter'))) : {},
          search: urlSearchParams.has('search') ? urlSearchParams.get('search') : '',
        }))
      }
    }
  }, [storageKey, navigate, urlSearchParams, setQueryParams, tableData?.defaultSortKey, tableData?.defaultSortDirection])

  return (
    <ActionTable 
      tableData={tableData}
      {...rest} 
      queryParams={queryParams} 
      setQueryParams={setQueryParams} 
    />
  )
}

export const ActionTableState =  ({ tableData, ...rest }) => {
  const [queryParams, setQueryParams] = useState({
    offset: 0,
    pageSize: 10,
    sort: {[tableData?.defaultSortKey]: tableData?.defaultSortDirection ?? 'asc'},
    filter: {},
  })

  return (
    <ActionTable 
      tableData={tableData}
      {...rest} 
      queryParams={queryParams} 
      setQueryParams={setQueryParams} 
    />
  )
}
